import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Radar, Plus, Search, X } from "lucide-react";
import { fetchScans, createScan, type Scan } from "../lib/api";
import { clsx } from "clsx";
import {
  PageHeader,
  StatusBadge,
  LoadingState,
  ErrorState,
  EmptyState,
  Button,
} from "../components/ui";

const MODES = [
  { value: "quick", label: "Quick", hint: "5–15 min" },
  { value: "standard", label: "Standard", hint: "30–60 min" },
  { value: "deep", label: "Deep", hint: "1–4 hrs" },
];

export default function Scans() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scans, setScans] = useState<Scan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [modeFilter, setModeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [showNew, setShowNew] = useState(Boolean((location.state as any)?.openNew));
  const [newTarget, setNewTarget] = useState("");
  const [newMode, setNewMode] = useState("quick");
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchScans()
      .then((data) => setScans(Array.isArray(data) ? data : []))
      .catch((e) => setError(e instanceof Error ? e.message : "Unknown error"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async () => {
    if (!newTarget.trim()) return;
    setCreating(true);
    try {
      await createScan({ target: newTarget.trim(), mode: newMode });
      setShowNew(false);
      setNewTarget("");
      load();
    } catch (e: any) {
      setError(e?.message || "Failed to launch scan");
    } finally {
      setCreating(false);
    }
  };

  const filtered = scans.filter((s) => {
    if (search && !s.target.toLowerCase().includes(search.toLowerCase()) && !s.id.includes(search)) return false;
    if (modeFilter !== "all" && s.mode !== modeFilter) return false;
    if (statusFilter !== "all" && s.status !== statusFilter) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Scans"
        description="Launch and monitor penetration tests against your targets."
      >
        <Button onClick={() => setShowNew((v) => !v)}>
          <Plus className="h-4 w-4" /> New scan
        </Button>
      </PageHeader>

      {showNew && (
        <div className="card animate-rise p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-base font-semibold tracking-tight text-mist-100">
              Launch a new scan
            </h2>
            <button onClick={() => setShowNew(false)} className="text-mist-500 hover:text-mist-200">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-mist-400">Target</label>
              <input
                className="field font-mono"
                placeholder="https://example.com  ·  github.com/org/repo  ·  192.168.1.10"
                value={newTarget}
                onChange={(e) => setNewTarget(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                autoFocus
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium text-mist-400">Depth</label>
              <div className="grid grid-cols-3 gap-2">
                {MODES.map((m) => (
                  <button
                    key={m.value}
                    onClick={() => setNewMode(m.value)}
                    className={clsx(
                      "rounded-lg border px-3 py-2.5 text-left transition-colors",
                      newMode === m.value
                        ? "border-volt-500 bg-volt-400/10"
                        : "border-line bg-ink-900 hover:border-line-strong",
                    )}
                  >
                    <span className={clsx("block text-sm font-medium", newMode === m.value ? "text-volt-300" : "text-mist-200")}>
                      {m.label}
                    </span>
                    <span className="block text-xs text-mist-500">{m.hint}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowNew(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={creating || !newTarget.trim()}>
                {creating ? "Launching…" : "Launch scan"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mist-500" />
          <input
            className="field pl-9"
            placeholder="Search targets…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className="field w-auto" value={modeFilter} onChange={(e) => setModeFilter(e.target.value)}>
          <option value="all">All modes</option>
          <option value="quick">Quick</option>
          <option value="standard">Standard</option>
          <option value="deep">Deep</option>
        </select>
        <select className="field w-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All status</option>
          <option value="running">Running</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="queued">Queued</option>
        </select>
      </div>

      {loading && <LoadingState label="Loading scans…" />}
      {error && <ErrorState message={error} onRetry={load} />}

      {!loading && !error && scans.length === 0 && (
        <EmptyState
          icon={<Radar className="h-6 w-6" />}
          title="No scans yet"
          description="Kick off your first penetration test — pick a target and Petir's agents will go to work."
          action={
            <Button size="sm" onClick={() => setShowNew(true)}>
              <Plus className="h-4 w-4" /> Launch a scan
            </Button>
          }
        />
      )}

      {!loading && !error && scans.length > 0 && (
        <div className="table-shell">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs uppercase tracking-wider text-mist-500">
                <th className="px-4 py-3 font-medium">Target</th>
                <th className="px-4 py-3 font-medium">Mode</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Findings</th>
                <th className="px-4 py-3 font-medium">Started</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-sm text-mist-400">
                    No scans match your filters.
                  </td>
                </tr>
              ) : (
                filtered.map((s) => (
                  <tr
                    key={s.id}
                    onClick={() => navigate(`/scans/${s.id}`)}
                    className="table-row cursor-pointer border-b border-line/60 last:border-0"
                  >
                    <td className="max-w-[300px] truncate px-4 py-3 font-mono text-xs text-mist-200">
                      {s.target}
                    </td>
                    <td className="px-4 py-3 capitalize text-mist-400">{s.mode}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={s.status} />
                    </td>
                    <td className="px-4 py-3 tabular-nums text-mist-300">{s.findings_count}</td>
                    <td className="px-4 py-3 text-mist-500">
                      {new Date(s.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
